/**
 * use-guidance-nudge — Orchestration des « coups de pouce » d'accompagnement.
 *
 * Un seul provider monté dans ProtectedShell (sous JulietteLauncherProvider) :
 * garantit qu'au plus UN conseil est visible à la fois, partagé entre l'orbe
 * desktop (sidebar) et l'orbe mobile (bottom nav). Toute la décision « quoi
 * montrer » est déléguée à la logique pure `selectNextNudge` ; ici on gère les
 * effets : lecture localStorage, délai d'apparition, et les 3 actions
 * (Fermer = report, Ne plus voir = définitif, CTA = action + acquittement).
 *
 * Garde-fous d'apparition :
 *  - le plafond « 1 par session » (sessionStorage) borne l'affichage dans la
 *    session courante ;
 *  - `AMBIENT_COOLDOWN_MS` est un filet INTER-sessions (deux ouvertures d'onglet
 *    rapprochées) : il n'est alimenté QUE par les conseils ambiants — un conseil
 *    de route ne « consomme » pas le rythme ambiant.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  GUIDANCE_NUDGES,
  GUIDANCE_NUDGE_CANDIDATES,
  type GuidanceNudge,
  type GuidanceNudgeKey,
} from "@/lib/coaching-messages";
import { selectNextNudge } from "@/lib/guidance-nudge-select";
import {
  dismissForever,
  getDismissedKeys,
  getLastNudgeAt,
  getShownKeysThisSession,
  getSnoozedUntil,
  isNudgesEnabled,
  markShownThisSession,
  setLastNudgeAt,
  snoozeNudge,
} from "@/lib/guidance-nudge-storage";
import { useJulietteLauncher } from "@/hooks/use-juliette-launcher";

/** Délai avant apparition de la bulle (laisser la page se poser). */
const APPEAR_DELAY_MS = 900;
/** Durée du report quand l'utilisateur clique « Fermer » (2 jours). */
const SNOOZE_MS = 1000 * 60 * 60 * 24 * 2;

interface GuidanceNudgeState {
  /** Conseil à afficher, ou null. */
  nudge: GuidanceNudge | null;
  /** « Fermer » (X) : reporte le conseil. */
  snooze: () => void;
  /** « Ne plus voir » : masque définitivement ce conseil. */
  dismiss: () => void;
  /** Exécute l'action du conseil puis l'acquitte (ne le remontre plus). */
  runCta: () => void;
}

const NULL_STATE: GuidanceNudgeState = {
  nudge: null,
  snooze: () => {},
  dismiss: () => {},
  runCta: () => {},
};

const GuidanceNudgeContext = createContext<GuidanceNudgeState | null>(null);

export function GuidanceNudgeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { open: openJuliette, isOpen: julietteOpen } = useJulietteLauncher();

  const [activeKey, setActiveKey] = useState<GuidanceNudgeKey | null>(null);
  // Miroir synchrone de activeKey : les callbacks d'action lisent la clé sans
  // exécuter d'effet de bord dans un updater setState (anti-pattern React).
  const activeKeyRef = useRef<GuidanceNudgeKey | null>(null);
  const timerRef = useRef<number | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showNudge = useCallback((key: GuidanceNudgeKey) => {
    activeKeyRef.current = key;
    setActiveKey(key);
  }, []);

  const clearNudge = useCallback(() => {
    activeKeyRef.current = null;
    setActiveKey(null);
  }, []);

  // À chaque changement de route (ou d'état d'ouverture de Juliette), on retire la
  // bulle courante puis on (re)calcule s'il y a un conseil à montrer ici.
  useEffect(() => {
    clearTimer();
    clearNudge();

    // Pas de conseil pendant qu'on parle déjà à Juliette (le panneau prend la place).
    if (julietteOpen) return;

    const key = selectNextNudge(GUIDANCE_NUDGE_CANDIDATES, {
      now: Date.now(),
      pathname,
      nudgesEnabled: isNudgesEnabled(),
      shownKeys: getShownKeysThisSession(),
      dismissedKeys: getDismissedKeys(),
      snoozedUntil: getSnoozedUntil(),
      lastNudgeAt: getLastNudgeAt(),
    }) as GuidanceNudgeKey | null;

    if (!key) return;

    timerRef.current = window.setTimeout(() => {
      showNudge(key);
      markShownThisSession(key);
      // Seuls les conseils ambiants alimentent le cooldown inter-sessions.
      if (GUIDANCE_NUDGES[key].scope === "ambient") {
        setLastNudgeAt(Date.now());
      }
      timerRef.current = null;
    }, APPEAR_DELAY_MS);

    return clearTimer;
  }, [pathname, julietteOpen, clearTimer, clearNudge, showNudge]);

  const snooze = useCallback(() => {
    const key = activeKeyRef.current;
    if (key) snoozeNudge(key, Date.now() + SNOOZE_MS);
    clearNudge();
  }, [clearNudge]);

  const dismiss = useCallback(() => {
    const key = activeKeyRef.current;
    if (key) dismissForever(key);
    clearNudge();
  }, [clearNudge]);

  const runCta = useCallback(() => {
    const key = activeKeyRef.current;
    if (key) {
      const cta = GUIDANCE_NUDGES[key].cta;
      if (cta) {
        if (cta.kind === "navigate") {
          navigate(cta.to);
        } else {
          openJuliette({ tab: cta.tab, prefill: cta.prefill });
        }
      }
      // L'utilisateur a engagé : on ne le remontre plus (acquittement définitif).
      dismissForever(key);
    }
    clearNudge();
  }, [navigate, openJuliette, clearNudge]);

  const value = useMemo<GuidanceNudgeState>(
    () => ({
      nudge: activeKey ? GUIDANCE_NUDGES[activeKey] : null,
      snooze,
      dismiss,
      runCta,
    }),
    [activeKey, snooze, dismiss, runCta],
  );

  return (
    <GuidanceNudgeContext.Provider value={value}>
      {children}
    </GuidanceNudgeContext.Provider>
  );
}

/**
 * Lit l'état du conseil courant. Tolérant : hors provider (tests isolés de la
 * sidebar / nav), renvoie un état neutre au lieu de jeter.
 */
export function useGuidanceNudge(): GuidanceNudgeState {
  return useContext(GuidanceNudgeContext) ?? NULL_STATE;
}
